import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, Cancel, Close } from '@mui/icons-material'
import { Box, Chip, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { SegmentOptionPopover } from '../SegmentOptionPopover/SegmentOptionPopover'
import { FILTER_CHIP_HEIGHT, FILTER_ROW_LABEL_WIDTH, filterChipSx } from '../helpers/filterChipStyles'
import { SegmentOption, SegmentOptionGroup, flattenGroups } from '../helpers/segmentFilterOptions'

const rowLabelSx = {
  fontSize: '0.75rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: 'text.secondary',
  fontWeight: 'bold',
  width: FILTER_ROW_LABEL_WIDTH,
  minWidth: FILTER_ROW_LABEL_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap' as const,
  // Match the chip height so the label lines up with the first chip line when
  // the chips wrap (the row is aligned to flex-start).
  lineHeight: `${FILTER_CHIP_HEIGHT}px`,
}

const chipsWrapSx = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  gap: 1,
  flex: '1 1 auto',
  minWidth: 0,
}

const countSx = {
  flexShrink: 0,
  lineHeight: `${FILTER_CHIP_HEIGHT}px`,
  color: 'text.secondary',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap' as const,
}

// Inherited scope, e.g. "All Casual genres": tinted like a selection but not
// interactive — it states what the row means, it isn't something to remove.
const ghostChipSx: SxProps<Theme> = [
  filterChipSx,
  (theme) => ({
    bgcolor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.dark,
    border: 'none',
    cursor: 'default',
  }),
]

const addChipSx: SxProps<Theme> = [
  filterChipSx,
  (theme) => ({
    borderStyle: 'dashed',
    borderWidth: 1.5,
    borderColor: theme.palette.divider,
    color: theme.palette.text.secondary,
    '& .MuiChip-icon': { color: 'inherit', ml: '6px', mr: '-6px' },
    '&:hover, &:focus-visible': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      bgcolor: alpha(theme.palette.primary.main, 0.04),
    },
  }),
]

export interface SegmentFilterRowProps {
  labelId: string
  label: string
  labelTooltip?: string
  // How deep this level sits in the hierarchy: 0 for Game Category, 1 for
  // Genre, 2 for Subgenre. Anything above 0 draws a "└" indented one step per
  // level, so the labels read as a tree.
  depth?: number
  selectedOptions: SegmentOption[]
  // Ghost chip copy for scope this row inherits from a parent selection.
  inheritedLabels: string[]
  // Shown alone when nothing is selected at this level or above it.
  allLabel: string
  // Options the "+ Add" popover offers, already scoped, pruned and ordered.
  groups: SegmentOptionGroup[]
  // Replaces this level's selection wholesale — the picker commits what it was
  // left holding rather than reporting each tick.
  onApply: (ids: string[]) => void
  // Clears this level (and, via the state hook, anything selected below it).
  onClear: () => void
  addAriaLabel: string
  clearAriaLabel: string
  searchPlaceholder: string
  containerSx?: SxProps<Theme>
}

/**
 * One level of the segment filter: label, the chips currently selected there
 * (each removable), ghost chips for inherited scope, a "+ Add" chip that opens
 * the picker, and a "selected of available" count on the right. Shows only what
 * is selected rather than every option, so the row's height doesn't grow with
 * the taxonomy.
 */
export const SegmentFilterRow: React.FC<SegmentFilterRowProps> = ({
  labelId,
  label,
  labelTooltip,
  depth = 0,
  selectedOptions,
  inheritedLabels,
  allLabel,
  groups,
  onApply,
  onClear,
  addAriaLabel,
  clearAriaLabel,
  searchPlaceholder,
  containerSx,
}) => {
  const { t } = useTranslation()
  const addChipRef = useRef<HTMLDivElement>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const availableOptions = useMemo(() => flattenGroups(groups), [groups])
  const availableCount = availableOptions.length
  const selectedIds = useMemo(() => selectedOptions.map((option) => option.id), [selectedOptions])
  // Nothing left to pick: every option the level offers is already selected
  // (e.g. both Sports and Driving under Sports & Driving). The "+ Add" chip then
  // turns into a "Clear" for this row, since removing is the only move left. With
  // no selection and no options either, it just sits disabled.
  const nothingToAdd = useMemo(() => availableOptions.every((option) => selectedIds.includes(option.id)), [availableOptions, selectedIds])
  const showClear = nothingToAdd && selectedOptions.length > 0
  // Prefix each selected chip with its parent only when the selection spans more
  // than one parent — "Casino · Slots" is noise while everything is under Casino.
  const showParentPrefix = useMemo(() => new Set(selectedOptions.map((option) => option.parentId)).size > 1, [selectedOptions])

  const openPicker = useCallback(() => setPickerOpen(true), [])
  const closePicker = useCallback(() => setPickerOpen(false), [])
  const removeOption = useCallback((id: string) => onApply(selectedIds.filter((selectedId) => selectedId !== id)), [onApply, selectedIds])

  const labelNode = (
    <Typography id={labelId} component="span" sx={{ ...rowLabelSx, ...(labelTooltip ? { cursor: 'help' } : {}) }}>
      {depth > 0 ? (
        <Box
          component="span"
          aria-hidden
          // The glyph's box-drawing baseline sits high against the uppercase label;
          // nudge it down so its horizontal bar lines up with the text's centre.
          sx={{ display: 'inline-block', width: 16, ml: (depth - 1) * 2, mr: 0.5, pl: 0.5, color: 'text.disabled', position: 'relative', top: 2 }}
        >
          └
        </Box>
      ) : null}
      {label}
    </Typography>
  )

  const showAllChip = selectedOptions.length === 0 && inheritedLabels.length === 0

  return (
    <Box sx={containerSx} role="group" aria-labelledby={labelId}>
      {labelTooltip ? (
        <GRTooltip content={labelTooltip} styles={{ display: 'inline-flex', flexShrink: 0 }}>
          {labelNode}
        </GRTooltip>
      ) : (
        labelNode
      )}

      <Box sx={chipsWrapSx}>
        {selectedOptions.map((option) => (
          <Chip
            key={option.id}
            size="small"
            color="primary"
            sx={filterChipSx}
            label={
              showParentPrefix && option.parentName ? (
                <>
                  <Box component="span" sx={{ opacity: 0.75, fontWeight: 'normal' }}>
                    {option.parentName} ·{' '}
                  </Box>
                  {option.name}
                </>
              ) : (
                option.name
              )
            }
            onDelete={() => removeOption(option.id)}
            // `titleAccess` gives the icon an accessible name (and drops MUI's default
            // aria-hidden), so the remove control announces what it removes.
            deleteIcon={<Cancel titleAccess={t('market-analytics:segment_filter_remove', { name: option.name })} />}
          />
        ))}
        {showAllChip ? <Chip size="small" component="span" label={allLabel} sx={ghostChipSx} /> : null}
        {inheritedLabels.map((inheritedLabel) => (
          <Chip key={inheritedLabel} size="small" component="span" label={inheritedLabel} sx={ghostChipSx} />
        ))}
        {showClear ? (
          <Chip
            size="small"
            variant="outlined"
            clickable
            icon={<Close fontSize="small" />}
            label={t('market-analytics:segment_filter_clear')}
            onClick={onClear}
            aria-label={clearAriaLabel}
            sx={addChipSx}
          />
        ) : (
          <Chip
            ref={addChipRef}
            size="small"
            variant="outlined"
            clickable
            disabled={nothingToAdd}
            icon={<Add fontSize="small" />}
            label={t('market-analytics:segment_filter_add')}
            onClick={openPicker}
            aria-label={addAriaLabel}
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
            sx={addChipSx}
          />
        )}
      </Box>

      <Typography component="span" variant="caption" sx={countSx}>
        {t('market-analytics:segment_filter_selected_of_available', { selected: selectedOptions.length, available: availableCount })}
      </Typography>

      <SegmentOptionPopover
        open={pickerOpen}
        anchorEl={addChipRef.current}
        onClose={closePicker}
        groups={groups}
        selectedIds={selectedIds}
        onApply={onApply}
        searchPlaceholder={searchPlaceholder}
        ariaLabel={addAriaLabel}
      />
    </Box>
  )
}
